import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  MenuItem
} from '@mui/material'
import { useDispatch } from 'react-redux'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'

import { createAccount } from '../../../actions'
import { textFieldTypes, userRoleTypes, variantTypes } from '../../../constants'
import { Logger, LoggerTypes, checkErrorResponse, isValidEmail } from '../../../utils'
import { Button, buttonModes, buttonTypes, FormTitle } from '../../../components'
import './style.scss'

const defaultValue = {
  email: null,
  role: 'USER'
}

export const UserCreateDialog = ({ setOpenDialog, onSave }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const [email, setEmail] = useState(location?.email || defaultValue.email)
  const [role, setRole] = useState(location?.role || userRoleTypes.USER)
  const [errors, setErrors] = useState(null)

  const onCloseDialog = () => setOpenDialog(false)

  const onChangeEmail = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'UserCreateDialog.onChangeEmail()', { email: _value })
    setEmail(_value)
  }

  const onChangeRole = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'UserCreateDialog.onChangeRole()', { role: _value })
    setRole(_value)
  }

  const onSaveClick = () => {
    const newAccount = {
      email: email === '' ? null : email,
      role: role === '' ? null : role
    }

    if (!isValidEmail(email)) {
      setErrors({ email: t('users.rights.email.invalid') })
      return
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'UserCreateDialog.onSaveClick()', { newAccount })

    dispatch(createAccount(newAccount))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'UserCreateDialog.onSaveClick().dispatch(createAccount()).then()', { res })
        onSave()
        onCloseDialog()
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'UserCreateDialog.onSaveClick().dispatch(createAccount()).catch()', { res })

        checkErrorResponse(res, enqueueSnackbar, t)

        if (res?.response?.data?.fields) {
          setErrors(res.response.data.fields)
        }
      })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'UserCreateDialog.render()', { email, role })

  return (
    <Dialog
      open={true}
      onClose={onCloseDialog} // <-- close while clicking on background
      maxWidth="xl"
      width="xl"
      className="UserCreateDialog"
    >
      <DialogContent>
        <FormTitle title={t('users.rights.create')} style={{ fontSize: '20px', width: '450px' }} />
        <form>
          <Grid container={true} spacing={2} className="common_fullwidth">
            <Grid item={true} xs={12} sm={12} className="common_fullwidth">
              {/*  -- email --  */}
              <Grid item xs={12} sm={12} className="common_fullwidth">
                <TextField
                  type={textFieldTypes.TEXT}
                  name="email"
                  id="email"
                  value={email}
                  label={t('users.rights.email.label')}
                  className="common_fullwidth"
                  onChange={onChangeEmail}
                  placeholder={t('users.rights.email.placeholder')}
                  variant={variantTypes.OUTLINED}
                  error={Boolean(errors && errors?.email)}
                  helperText={(errors && errors?.email) || ''}
                />
              </Grid>

              {/* -- role -- */}
              <Grid item xs={12} sm={12} className="common_fullwidth roleContainer">
                <FormControl error={Boolean(errors && errors?.role)}>
                  <InputLabel className="roleLabel">{t('users.rights.role.label')}</InputLabel>
                  <Select
                    className="common_fullwidth roleSelect"
                    value={role}
                    defaultValue={userRoleTypes.USER}
                    onChange={onChangeRole}
                    label=""
                    variant={variantTypes.OUTLINED}
                  >
                    {Object.keys(userRoleTypes).map((_role, index) => (
                      <MenuItem key={`role-${index}`} value={_role}>
                        {t(`users.rights.role.types.${_role}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{(errors && errors?.role) || ''}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onCloseDialog}
          className="button"
        />
        <Button
          mode={buttonModes.PRIMARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.save')}
          icon={<SaveIcon />}
          onClick={onSaveClick}
          className="button"
        />
      </DialogActions>
    </Dialog>
  )
}

UserCreateDialog.displayName = 'UserCreateDialog'

UserCreateDialog.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
}

UserCreateDialog.defaultProps = {
  setOpenDialog: () => {},
  onSave: () => {}
}
